import React, { useRef, useState } from "react";
import { Upload, Link2, X } from "lucide-react";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Image input field that supports BOTH:
 *  - Click/drop a file to upload (POST /api/admin/upload → server stores it)
 *  - Paste a public URL
 *
 * Shows a live preview, replace/clear actions, and disabled-state while uploading.
 *
 * Usage:
 *   <ImageUploadField label="Hero image" value={form.image_url} onChange={(url) => setForm({...form, image_url: url})} />
 */
export default function ImageUploadField({
  label = "Image",
  value = "",
  onChange,
  testid = "image-upload",
  required = false,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("upload"); // "upload" | "url"

  const previewSrc = resolvePreviewUrl(value);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <label className="field-label !mb-0">
          {label} {required && <span className="text-[#DE9E48] normal-case">(required)</span>}
        </label>
        <div className="flex gap-1">
          <button
            type="button"
            data-testid={`${testid}-mode-upload`}
            onClick={() => {
              if (mode === "upload") {
                inputRef.current?.click();
              } else {
                setMode("upload");
              }
            }}
            className={`px-2 py-1 text-[0.6rem] font-overline rounded ${
              mode === "upload" ? "bg-[#DE9E48] text-stone-50" : "text-stone-400 hover:text-[#DE9E48]"
            }`}
          >
            <Upload size={10} className="inline mr-1" /> Upload
          </button>
          <button
            type="button"
            data-testid={`${testid}-mode-url`}
            onClick={() => setMode("url")}
            className={`px-2 py-1 text-[0.6rem] font-overline rounded ${
              mode === "url" ? "bg-[#DE9E48] text-stone-50" : "text-stone-400 hover:text-[#DE9E48]"
            }`}
          >
            <Link2 size={10} className="inline mr-1" /> URL
          </button>
        </div>
      </div>

      {mode === "upload" ? (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          className={`border border-dashed border-white/15 rounded-md p-4 cursor-pointer hover:border-[#DE9E48] transition-colors bg-stone-950/40 ${
            uploading ? "opacity-60 pointer-events-none" : ""
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testid}-file-input`}
          />
          <div className="flex items-center gap-3">
            <Upload size={18} className="text-[#DE9E48] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-overline text-[0.65rem] text-stone-300">
                {uploading ? "Uploading…" : "Click to choose, or drop an image here"}
              </p>
              <p className="font-body text-[0.7rem] text-stone-500 mt-0.5">
                JPG, PNG, WebP, or GIF · up to 10 MB
              </p>
            </div>
          </div>
        </div>
      ) : (
        <input
          data-testid={`${testid}-url-input`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or /api/images/…"
          className="field-input"
        />
      )}

      {error && (
        <p className="mt-2 font-overline text-[0.65rem] text-red-400">{error}</p>
      )}

      {previewSrc && (
        <div className="mt-3 relative inline-block border border-stone-900/15 bg-stone-900/20">
          <img
            src={previewSrc}
            alt=""
            className="max-h-40 w-auto object-contain block"
            onError={(e) => (e.currentTarget.style.opacity = 0.3)}
          />
          <button
            type="button"
            data-testid={`${testid}-clear`}
            onClick={() => onChange("")}
            className="absolute top-1 right-1 w-6 h-6 bg-stone-950/90 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"
            aria-label="Clear image"
          >
            <X size={12} className="text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

function resolvePreviewUrl(value) {
  if (!value) return "";
  // Relative /api/images/... → prepend BACKEND_URL so the <img> can load it
  if (value.startsWith("/api/")) return `${BACKEND_URL}${value}`;
  return value;
}
